import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ConcurrenceEnums } from 'src/app/enum/concurrence-enums';
import { UserModel } from 'src/app/models/authentication/user.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoicingCutoffService } from 'src/app/services/invoicing/invoicing-cutoff.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ConcurrenceService } from 'src/app/services/utilities/concurrence.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-close-concurrence',
  templateUrl: './close-concurrence.component.html',
  styleUrls: ['./close-concurrence.component.scss'],
  standalone: false
})
export class CloseConcurrenceComponent implements OnInit, OnDestroy {
  @ViewChild('information') informationPopover!: Popover;

  formCloseConcurrence: FormGroup = new FormGroup({});
  listBranches: any[] = [];
  isOpened: string | undefined = undefined;
  listConcurrences: any[] = [
    { label: "Prefacturación", value: 1 },
    { label: "Facturación", value: 2 },
    { label: "Ocupación", value: 3 },
    { label: "Desbloquear Corte", value: 4 }
  ];
  nameWindow: string = 'Desbloquear Proceso';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = "unlock-process";
  userAuthenticated: UserModel = new UserModel();

  constructor(private branchService: BranchService,
    private concurrenceService: ConcurrenceService,
    private formBuilder: FormBuilder,
    private invoicingCutoffService: InvoicingCutoffService,
    private router: Router,
    private headerService: HeaderService,
    private toastHandlerService: ToastHandlerService,
    private userAuthenticatedService: UserAuthenticatedService,
    private title: Title,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    this.getPermission();
    await this.validateForm();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async getBranches() {
    try {
      const [branches] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
      ]);
      this.listBranches = branches;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
    }
  }

  closeConcurrence() {
    if (this.formCloseConcurrence.valid) {
      const formCloseConcurrence = this.formCloseConcurrence.value;
      Swal.fire({
        title: 'Atención',
        text: "¿Realmente desea desbloquear el proceso seleccionado?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Si',
        cancelButtonText: 'No'
      }).then(async (result) => {
        if (result.isConfirmed) {
          if (formCloseConcurrence.concurrenceCode == ConcurrenceEnums.desbloquearCortes) {
            await firstValueFrom(this.invoicingCutoffService.unlockInvoicingCutoffByBranchId(formCloseConcurrence.branchId));
            this.toastHandlerService.showToast('success', `Todos los cortes fueron desbloqueados exitosamente.`);
          } else {
            await firstValueFrom(this.concurrenceService.closeConcurrence(formCloseConcurrence.branchId, formCloseConcurrence.concurrenceCode));
            this.toastHandlerService.showToast('success', `La concurrencia fue cerrada exitosamente.`);
          }
        }
      });

    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Atención.',
        text: 'Todos los campos marcados con (*) son obligatorios.'
      });
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const response = await firstValueFrom(this.userAuthenticatedService.branchObservable.pipe(filter(branch => branch && branch.branchId !== 0)));
      setTimeout(() => {
        this.formCloseConcurrence.controls["branchId"].patchValue(response.branchId);
      }, 100);
    } catch (error) {
      console.error('Error to get first branch by user authenticated:', error);
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos por tipo de impuesto.', error);
    }
    this.getPermissionsForUserByWindow();
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(
        filter(user => user && user.userId !== 0)
      )
    );
  }

  openToggle(event: any) {
    this.informationPopover.toggle(event);
  }

  async validateForm() {
    this.formCloseConcurrence = this.formBuilder.group({
      branchId: [0, [Validators.required, Validators.min(1)]],
      concurrenceCode: [ConcurrenceEnums.prefacturacion, [Validators.required]]
    });
    await this.getBranches();
    await this.getFirstBranchByUserAuthenticatedAsync();
  }
}
